import * as THREE from 'three';

// The moon: a lit sphere at infinity, in the camera-locked sky with the sun and the stars.
//
// The server gives its direction in the world's local frame, its angular size, the phase angle
// (star-moon-world: 0 is full, pi is new) and the beam colour it delivers to the ground, which already
// carries the atmosphere's dimming and reddening. The disc is shaded from the direction of the sun, so
// the phase is the geometry and not a texture: a point is lit by cos(i) / (cos(i) + cos(e)), the
// Lommel-Seeliger law, which keeps a full moon flat like the real one instead of limb-darkened like a
// Lambert ball. A few low-frequency dark patches stand in for the maria.
//
// Brightness. Disc-average surface brightness in the sky's units (irradiance-equivalent) is the beam
// divided by the disc's solid angle over pi, i.e. beam / r^2 with r the angular radius. It goes through
// the same sky curve, 1 - exp(-SKY_EXPOSURE * gain * E), so in daylight the moon is a pale ghost and
// at night, with the display gain up, it is a blaze. The curve's argument is capped so the shading
// stays readable on a blazing disc rather than clipping to a white coin.
//
// The moon's light is added to the sky in front of it (a screen blend, like the stars), not painted over
// it: the atmosphere between the viewer and the moon scatters light of its own, so a new moon is
// invisible against a day sky and a thin crescent is only its lit sliver. The disc still occludes the
// stars behind it.
//
// Size. The moon is half a degree across, as in the sky, which is a few pixels on a wide field of view.
// MOON_SIZE_SCALE enlarges the drawn disc (not its brightness); 1 is the true size.

export interface MoonState {
  direction: ArrayLike<number>;     // world-local frame (x east, y north, z up)
  angularDiameter: number;          // radians
  phase: number;                    // star-moon-world angle, radians
  rgb: ArrayLike<number>;           // beam irradiance at the ground, after the atmosphere
}

const SKY_EXPOSURE = 6.0;           // must match the renderer's skylight -> colour curve
const DISC_X_MAX   = 1.8;           // cap on the curve's argument at the lit centre of the disc
export const MOON_SIZE_SCALE = 2.0; // drawn size relative to the true angular size
export const MOON_GLARE_RADIUS = 12; // glare plane half-width, in drawn disc radii (as the sun's is)
export const MOON_GLARE_STRENGTH = 0.35; // peak opacity of the glare at a bright moon; 0 turns it off
const MOON_GLARE_KNEE = 0.02;       // x at which the glare is half strength (x = SKY_EXPOSURE * gain * beam luminance)

const VERT = /* glsl */ `
varying vec3 vN;
void main() {
  vN = normal;                       // object space = sky axes (the mesh is never rotated)
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAG = /* glsl */ `
uniform vec3  uSunDir;              // toward the sun, three axes
uniform vec3  uViewDir;             // from the moon toward the viewer, three axes
uniform vec3  uE;                   // disc-average surface brightness, per channel, in the sky's units
uniform float uEGain;               // SKY_EXPOSURE * the display exposure gain
uniform float uMeanShade;           // disc-average of the shading term, for this phase
uniform float uSrgbOut;
varying vec3 vN;

float hash(vec3 p) { return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453); }
float vnoise(vec3 p) {
  vec3 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = mix(mix(hash(i), hash(i + vec3(1,0,0)), f.x), mix(hash(i + vec3(0,1,0)), hash(i + vec3(1,1,0)), f.x), f.y);
  float b = mix(mix(hash(i + vec3(0,0,1)), hash(i + vec3(1,0,1)), f.x), mix(hash(i + vec3(0,1,1)), hash(i + vec3(1,1,1)), f.x), f.y);
  return mix(a, b, f.z);
}
vec3 encodeSrgb(vec3 c) {
  return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c));
}

void main() {
  vec3 n = normalize(vN);
  float ce = dot(n, uViewDir);
  if (ce <= 0.0) discard;                                   // far hemisphere
  float ci = dot(n, uSunDir);
  float shade = ci > 0.0 ? ci / (ci + ce) : 0.0;            // Lommel-Seeliger
  float maria = 0.72 + 0.28 * smoothstep(0.35, 0.65, 0.65 * vnoise(n * 2.3 + 4.0) + 0.35 * vnoise(n * 5.1 + 9.0));
  float k = shade * maria / max(uMeanShade, 1e-3);          // 1 = the disc's average brightness
  vec3 x = min(uEGain * uE, vec3(${DISC_X_MAX.toFixed(2)})) * k;
  vec3 s = 1.0 - exp(-x);
  if (uSrgbOut > 0.5) s = encodeSrgb(s);
  gl_FragColor = vec4(s, 1.0);
}
`;

export class MoonView {
  readonly disc: THREE.Mesh;
  readonly halo: THREE.Mesh;
  private discGeo = new THREE.SphereGeometry(1, 48, 24);
  private haloGeo = new THREE.PlaneGeometry(2, 2);
  private discMat: THREE.ShaderMaterial;
  private haloMat: THREE.ShaderMaterial;

  constructor(private skyRadius: number) {
    this.discMat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: true,                   // the disc hides the stars behind it, lit or not
      blending: THREE.CustomBlending,     // screen: dst + src * (1 - dst)
      blendEquation: THREE.AddEquation,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneMinusSrcColorFactor,
      uniforms: {
        uSunDir:    { value: new THREE.Vector3(0, 1, 0) },
        uViewDir:   { value: new THREE.Vector3(0, -1, 0) },
        uE:         { value: new THREE.Vector3() },
        uEGain:     { value: SKY_EXPOSURE },
        uMeanShade: { value: 0.5 },
        uSrgbOut:   { value: 1 },
      },
    });
    this.disc = new THREE.Mesh(this.discGeo, this.discMat);
    this.disc.frustumCulled = false;
    this.disc.renderOrder = -4;           // before the stars, which it occludes
    this.disc.visible = false;

    // A soft radial glow, drawn facing the camera.
    this.haloMat = new THREE.ShaderMaterial({
      vertexShader: 'varying vec2 vUv;\nvoid main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: `
        uniform vec3 uColor; uniform float uOpacity; uniform float uSrgbOut; varying vec2 vUv;
        vec3 encodeSrgb(vec3 c) { return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c)); }
        void main() {
          // the same profile as the sun's glare (sunGlare.ts): a tight core falling roughly as an inverse
          // square, a faint wide veil, and nothing at the rim. r = 1 / MOON_GLARE_RADIUS is the disc's edge.
          float r = length(vUv * 2.0 - 1.0);
          float core = 1.0 / (1.0 + pow(r / 0.11, 2.0));
          float veil = exp(-pow(r / 0.42, 2.0));
          float a = uOpacity * (0.55 * core + 0.30 * veil) * (1.0 - smoothstep(0.6, 1.0, r));
          vec3 c = uSrgbOut > 0.5 ? encodeSrgb(uColor) : uColor;
          gl_FragColor = vec4(c, a);
        }`,
      uniforms: { uColor: { value: new THREE.Color(1, 1, 1) }, uOpacity: { value: 0 }, uSrgbOut: { value: 1 } },
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    this.halo = new THREE.Mesh(this.haloGeo, this.haloMat);
    this.halo.frustumCulled = false;
    this.halo.renderOrder = -3;
    this.halo.visible = false;
  }

  /** Disc-average of Lommel-Seeliger shading over the visible disc, by midpoint quadrature. The
   *  normal on the disc at cos(e) = mu, azimuth phi, in a frame with the viewer on +z and the sun at
   *  angle `phase` from it, is lit by cos(i) = sin(theta) cos(phi) sin(phase) + mu cos(phase). */
  static meanShade(phase: number): number {
    const NM = 10, NP = 24;
    const sp = Math.sin(phase), cp = Math.cos(phase);
    let sum = 0, wsum = 0;
    for (let a = 0; a < NM; a++) {
      const mu = (a + 0.5) / NM, st = Math.sqrt(1 - mu * mu);
      for (let b = 0; b < NP; b++) {
        const ci = st * Math.cos((b + 0.5) * 2 * Math.PI / NP) * sp + mu * cp;
        sum += (ci > 0 ? ci / (ci + mu) : 0) * mu;       // weight by mu: equal areas on the projected disc
        wsum += mu;
      }
    }
    return sum / wsum;
  }

  /** Per frame. `sunDirThree` is the direction to the home star in three's axes; `gain` the display
   *  exposure gain. `moon` null hides the moon. */
  update(cameraPos: THREE.Vector3, moon: MoonState | null, sunDirThree: THREE.Vector3, gain: number, srgbOut: boolean) {
    if (!moon || !(moon.angularDiameter > 0)) { this.disc.visible = false; this.halo.visible = false; return; }
    const [dx, dy, dz] = [moon.direction[0], moon.direction[1], moon.direction[2]];
    const tx = dx, ty = dz, tz = -dy;                       // sim (x, y, z) -> three (x, z, -y)
    const angR = 0.5 * moon.angularDiameter;
    const R = this.skyRadius * Math.tan(angR) * MOON_SIZE_SCALE;
    const px = cameraPos.x + tx * this.skyRadius, py = cameraPos.y + ty * this.skyRadius, pz = cameraPos.z + tz * this.skyRadius;
    this.disc.position.set(px, py, pz); this.disc.scale.setScalar(R);
    this.halo.position.set(px, py, pz); this.halo.scale.setScalar(R * MOON_GLARE_RADIUS);
    this.halo.lookAt(cameraPos);

    const u = this.discMat.uniforms;
    (u.uSunDir.value as THREE.Vector3).copy(sunDirThree);
    (u.uViewDir.value as THREE.Vector3).set(-tx, -ty, -tz);
    const r2 = angR * angR;
    (u.uE.value as THREE.Vector3).set(moon.rgb[0] / r2, moon.rgb[1] / r2, moon.rgb[2] / r2);
    u.uEGain.value = SKY_EXPOSURE * gain;
    u.uMeanShade.value = MoonView.meanShade(moon.phase);
    u.uSrgbOut.value = srgbOut ? 1 : 0;
    this.disc.visible = true;

    // Glare: a glow whose strength follows how bright the moon's beam is on screen (a full moon at night
    // glows clearly, a crescent faintly, and by day not at all), tinted by the beam colour (so a low moon
    // glows orange). Scatter in the eye and the air scales with the light received, not the disc's own
    // surface brightness, so this uses the beam irradiance.
    const m = Math.max(moon.rgb[0], moon.rgb[1], moon.rgb[2]);
    const lumBeam = 0.2126 * moon.rgb[0] + 0.7152 * moon.rgb[1] + 0.0722 * moon.rgb[2];
    const hu = this.haloMat.uniforms;
    if (m > 0) (hu.uColor.value as THREE.Color).setRGB(moon.rgb[0] / m, moon.rgb[1] / m, moon.rgb[2] / m);
    const x = SKY_EXPOSURE * gain * lumBeam;
    hu.uOpacity.value = MOON_GLARE_STRENGTH * (x / (x + MOON_GLARE_KNEE));
    hu.uSrgbOut.value = srgbOut ? 1 : 0;
    this.halo.visible = hu.uOpacity.value > 0.003;
  }

  dispose() {
    this.discGeo.dispose(); this.haloGeo.dispose();
    this.discMat.dispose(); this.haloMat.dispose();
  }
}
